import { Moment } from "moment";
import Utils from "../../core/shared/utils";
import { SyncModel, SyncDataModel, DataModel } from "../../core/shared/models/syncModel";
import { SOTProductViewModel } from "./product.model";
import { InternationalisationService } from "../../core/forked/angular-internationalisation";
import { SOTRecipeLinkingViewModel } from "./recipeLinking.model";
import { RecipeLinkingDataModel, WebshopLanguagesDataModel } from "../../core/shared/models";
import { ContentType, ImageOrientation, TranslationContentType } from "../../core/shared/contentTypes";
import { ISOTProductViewModel } from "./product.facade";
import { CoreLanguageManager } from "../../core/services/ILanguageManager";

// ViewModel
export class SOTItemGroupViewModel {
  public get id(): number {
    return this._model.id;
  }

  public get label(): string {
    const label = CoreLanguageManager.translate(TranslationContentType.SOT_ItemGroup_Label, this._model.id, this._model.label_de);
    return label ? label : this._model.label_de;
  }

  public get description(): string {
    const description = CoreLanguageManager.translate(TranslationContentType.SOT_ItemGroup_Description, this._model.id, this._model.description_de);
    return description ? description : this._model.description_de;
  }

  public products: ISOTProductViewModel[];
  private sotProducts: SOTProductViewModel[];
  private sotRecipeLinkings: SOTRecipeLinkingViewModel[];
  public recommendations: SOTItemGroupRecommendationViewModel[];

  private _model: SOTItemGroupDataModel2;
  public get model(): SOTItemGroupDataModel2 {
    return this._model;
  }

  constructor(model: SOTItemGroupDataModel2,
    ordertype: "in_house" | "pickup", sotProducts: SOTProductViewModel[],
    recipeLinkings: RecipeLinkingDataModel[],
    recipeLinkingsHideProductIDs: number[],
    productTagFilter: number[],
    sortFunction: (a: ISOTProductViewModel, b: ISOTProductViewModel) => number) {
    const me = this;
    if (Utils.IsNull(model) || model.deleted || !model.active) {
      throw new Error("model is wrong");
    }
    me._model = model;
    // gets products if there are saleable
    me.sotProducts = sotProducts.filter((product) => product.model.saleable);

    const filterGroup = (group) => { return group.content_id === model.id && group.content_type === ContentType.SOTItemGroup };
    if (ordertype === "in_house") {
      me.sotRecipeLinkings = recipeLinkings.filter((rule) => rule.sot_ordertype_inhouse && rule.groups.find(filterGroup)).map((recipeLinking) => new SOTRecipeLinkingViewModel(recipeLinking, me.sotProducts, productTagFilter, sortFunction));
    } else /* if (ordertype === "pickup") */ {
      me.sotRecipeLinkings = recipeLinkings.filter((rule) => rule.sot_ordertype_takeaway && rule.groups.find(filterGroup)).map((recipeLinking) => new SOTRecipeLinkingViewModel(recipeLinking, me.sotProducts, productTagFilter, sortFunction));
    }

    // filter products if there in article_group_ids
    me.sotProducts = me.sotProducts.filter((product) => model.article_group_ids.some((article_group_id) => product.model.inGroup([article_group_id])));
    // entferne nun die Rezepturverkettungs Artikel, die verstekt werden sollen
    // die versteckte ID wird nicht länger im Main menu angezeigt und das Produkt ist nur über die Rezepturverkettung zu finden
    me.sotProducts = me.sotProducts.filter((product) => !recipeLinkingsHideProductIDs.includes(product.product_id));
    // entferne nun die Rezepturverkettung ohne Artikel
    me.sotRecipeLinkings = me.sotRecipeLinkings.filter((link) => link.hasOriginalSotProducts);

    me.products = [...me.sotProducts, ...me.sotRecipeLinkings];

    me.recommendations = [];
    const model_recomindations = model.recommendations.filter((item) => item.active).sort((l, r) => l.sort_no - r.sort_no)
    model_recomindations.forEach(recommendation => {
      const view_model = new SOTItemGroupRecommendationViewModel(recommendation, model, sotProducts);
      if (view_model.products.length > 0) {
        me.recommendations.push(view_model);
      }
    });
  }
}

export class SOTItemGroupRecommendationViewModel {
  public products: SOTProductViewModel[];

  private _model: SOTItemGroupRecommendation2;
  public get model(): SOTItemGroupRecommendation2 {
    return this._model;
  }

  private _parent: SOTItemGroupDataModel2;

  public get title(): string {
    const recomindation_id = WebshopLanguagesDataModel.GetSOTItemGroupRecommendationArticleGroup_ContentID(this._model.sort_no, this._parent.id);
    const title = CoreLanguageManager.translate(TranslationContentType.SOT_ItemGroup_RecommendationArticleGroup_Title, recomindation_id, this._model.title_de);
    return title ? title : "";
  }

  constructor(model: SOTItemGroupRecommendation2, parent: SOTItemGroupDataModel2, sotProducts: SOTProductViewModel[]) {
    const me = this;
    if (Utils.IsNull(model) || !model.active) {
      throw new Error("model is wrong");
    }
    me._model = model;
    me._parent = parent;
    // gets products if there in article_group_id
    me.products = sotProducts.filter((product) => product.model.saleable && product.model.inGroup([model.article_group_id]));
  }
}

// Requests
export class SOTItemGroupSyncModel extends SyncModel {
  public hyp_cus_nr: number;
}

// Responses
export class SOTItemGroupDataModel2 extends DataModel {
  public get DM_Key(): number | string { return this.id; }
  public id: number;
  public active: boolean;
  public sort_no: number;
  public label_de: string;
  public description_de: string;
  public article_group_ids: number[];
  public recommendations: SOTItemGroupRecommendation2[];
  public deleted: boolean;
  public change_at_ymd: number;
  public change_at_hms: number;
  public get change_at(): Moment {
    return Utils.GetMomentFromInt(this.change_at_ymd, this.change_at_hms);
  }

  public last_image_update_at_ymd: number;
  public last_image_update_at_hms: number;
  /** Hat die Warengruppe ein Bild */
  public get has_image(): boolean {
    return this.last_image_update_at_ymd > 0;
  }
  /** ya_image Pfad zum Bild */
  public get image_path(): string {
    return !this.has_image ? "" : ContentType.SOTItemGroup + "/" + this.id + "_photo_" + ImageOrientation.Landscape + "/" + this.last_image_update_at_ymd + "/" + this.last_image_update_at_hms;
  }

  public DeserializeSubTypes(): void {
    const me = this;
    // not convertet for performance, because SOTItemGroupRecommendation is simple type
    //if (Array.isArray(me.recommendations)) {
    //  const recommendations = me.recommendations;
    //  for (let i = 0; i < recommendations.length; ++i) {
    //    const recommendation = recommendations[i];
    //    if (Object.getPrototypeOf(recommendation) !== SOTItemGroupRecommendation) {
    //      recommendations[i] = Utils.GetExtend(new SOTItemGroupRecommendation(), recommendation);
    //    }
    //  }
    //}
  }
}

export class SOTItemGroupRecommendation2 {
  public active: boolean;
  public sort_no: number;
  public article_group_id: number;
  public title_de: string;
}

export class SOTItemGroupSyncDataModel2 extends SyncDataModel {
  public item_groups: SOTItemGroupDataModel2[];
  public get models(): DataModel[] { return this.item_groups; }
  public DeserializeType(): void {
    this._DeserializeType(SOTItemGroupDataModel2);
  }
}
